import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormField, TextField, CountrySelectField } from 'modules/Common/form/v2';
import { CountryDropdown } from 'modules/Common/utils/Utility';
import { isUkRegulation } from 'modules/Common/utils/regulation';

/**
 * The declarant record's fields, one component per group, rendered inside a v2
 * `<Form>` by every surface that edits them (Declarant Info tab, UK
 * registration summary — backend docs/features/uk-cbam-registration-summary
 * D16). Rules and payload live in `config/declarantInfo.js`. `label` is a
 * surface's own wording for a field; `hideLabel` keeps it for screen readers
 * only, where the label is already on screen (an inline edit in a labelled row).
 */

const Hint = ({ children }) => <span className="text-xs text-slate-500">{children}</span>;

export function DeclarantNameField({ label, hideLabel }) {
  const { t } = useTranslation('app');
  return (
    <FormField
      name="declarantName"
      label={label ?? t('declarantInfo.name', 'Name')}
      hideLabel={hideLabel}
      required
      colSpan={8}
    >
      <TextField />
    </FormField>
  );
}

export function EoriField({ help, hideLabel }) {
  const { t } = useTranslation('app');
  return (
    <FormField
      name="EORI"
      label={t('declarantInfo.eori', 'EORI Number')}
      hideLabel={hideLabel}
      hint={help ? <Hint>{help}</Hint> : null}
      required
      colSpan={4}
    >
      <TextField />
    </FormField>
  );
}

export function SicCodeField({ disabled, hideLabel }) {
  const { t } = useTranslation('app');
  return (
    <FormField
      name="sicCode"
      label={t('declarantInfo.sicCode', 'SIC code')}
      hideLabel={hideLabel}
      hint={
        <Hint>
          {t(
            'declarantInfo.sicCodeHelp',
            '5 digits, UK SIC 2007 — for example 24100. Find yours on your Companies House record.',
          )}
        </Hint>
      }
      colSpan={4}
    >
      <TextField
        inputMode="numeric"
        maxLength={5}
        disabled={disabled}
      />
    </FormField>
  );
}

export function ContactNameField({ label, hideLabel }) {
  const { t } = useTranslation('app');
  return (
    <FormField
      name="contactName"
      label={label ?? t('declarantInfo.contactName', 'Contact name')}
      hideLabel={hideLabel}
      colSpan={6}
    >
      <TextField />
    </FormField>
  );
}

export function ContactEmailField({ label, hideLabel }) {
  const { t } = useTranslation('app');
  return (
    <FormField
      name="contactEmail"
      label={label ?? t('declarantInfo.contactEmail', 'Contact email')}
      hideLabel={hideLabel}
      colSpan={6}
    >
      <TextField type="email" />
    </FormField>
  );
}

export function ContactFields() {
  return (
    <>
      <ContactNameField />
      <ContactEmailField />
    </>
  );
}

export function AddressFields({ declarant }) {
  const { t } = useTranslation('app');
  return (
    <>
      <FormField
        name="country"
        label={t('declarantInfo.country', 'Country')}
        required
        colSpan={12}
      >
        {/* An EU declarant is established in the EU; a UK one is not —
            the EU list would not even offer United Kingdom. */}
        <CountrySelectField
          scope={isUkRegulation(declarant) ? CountryDropdown.ALL : CountryDropdown.EU}
          valueAs="id"
        />
      </FormField>
      <FormField
        name="state"
        label={t('declarantInfo.state', 'State')}
        colSpan={6}
      >
        <TextField />
      </FormField>
      <FormField
        name="city"
        label={t('declarantInfo.city', 'City')}
        required
        colSpan={6}
      >
        <TextField />
      </FormField>
      <FormField
        name="street"
        label={t('declarantInfo.street', 'Street')}
        colSpan={6}
      >
        <TextField />
      </FormField>
      <FormField
        name="streetNumber"
        label={t('declarantInfo.streetNumber', 'Street Num')}
        colSpan={3}
      >
        <TextField />
      </FormField>
      <FormField
        name="postcode"
        label={t('declarantInfo.postcode', 'Postcode')}
        colSpan={3}
      >
        <TextField />
      </FormField>
      <FormField
        name="additionalLine"
        label={t('declarantInfo.additionalLine', 'Additional Line')}
        colSpan={12}
      >
        <TextField />
      </FormField>
      <FormField
        name="poBox"
        label={t('declarantInfo.poBox', 'P.O. Box')}
        colSpan={12}
      >
        <TextField />
      </FormField>
    </>
  );
}
